import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import type { OnyxChip } from '@onyx/angular';
import {
  OnyxChipsComponent,
  OnyxSectionComponent,
  OnyxToggleComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { linkedChipSignal } from '../../../../../common/utilities/linked-chip-signal';
import type { ContractorBranchForm } from '../contractor-branches-form/contractor-branch-form-modal/contractor-branch-form-modal.component';
import { ContractorShippingTypeFormComponent } from '../contractor-shipping-type-form/contractor-shipping-type-form.component';
import type { ContractorInvoiceFormGroup } from './contractor-invoice-form/contractor-invoice-form.component';
import { ContractorInvoiceFormComponent } from './contractor-invoice-form/contractor-invoice-form.component';

export type ContractorInvoicesFormArray = ReturnType<
  (typeof ContractorInvoicesFormComponent)['buildForm']
>;

@Component({
  selector: 'app-contractor-invoices-form',
  imports: [
    OnyxChipsComponent,
    OnyxSectionComponent,
    ContractorInvoiceFormComponent,
    OnyxToggleComponent,
    OnyxTooltipDirective,
  ],
  templateUrl: './contractor-invoices-form.component.html',
  styleUrl: './contractor-invoices-form.component.scss',
})
export class ContractorInvoicesFormComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly injector = inject(Injector);

  public readonly form = input.required<ContractorInvoicesFormArray>();
  public readonly branches = input.required<ContractorBranchForm[]>();
  public readonly country = input.required<string | null>();

  protected readonly I18N = 'contractors.contractorForm.invoices';

  protected chips = computed<OnyxChip<string>[]>(() =>
    this.branches().map((branch) => ({
      name: branch.name!,
      value: branch.uuid!,
    })),
  );

  protected selectedBranch = linkedChipSignal(() => this.chips());
  protected selectedBranchForm = computed(() =>
    this.form().controls.find(
      ({ value }) => value.branchUuid === this.selectedBranch()[0],
    ),
  );
  protected mainBranchForm = computed(() =>
    this.form().controls.find(
      ({ value }) => value.branchUuid === this.branches()[0].uuid,
    )!,
  );

  public ngOnInit(): void {
    effect(
      () => {
        const form = this.form();
        const branches = new Set(this.branches().map((branch) => branch.uuid!));

        for (const control of form.controls) {
          if (branches.has(control.value.branchUuid!)) continue;
          form.removeAt(form.controls.indexOf(control));
        }
      },
      { injector: this.injector },
    );
  }

  public focusInvalidForm(): void {
    if (this.selectedBranchForm()?.invalid) return;

    for (const branch of this.form().controls) {
      if (branch.valid) continue;

      this.selectedBranch.set([branch.controls.branchUuid.getRawValue()]);
      return;
    }
  }

  protected addBranchForm(): void {
    const template = this.mainBranchForm().getRawValue();
    const branchUuid = this.selectedBranch()[0];

    this.form().push(
      ContractorInvoiceFormComponent.buildForm(this.fb, branchUuid),
    );

    const branchForm = this.form().at(-1);
    for (const _ of template.shippingType.emails) {
      branchForm.controls.shippingType.controls.emails.push(
        ContractorShippingTypeFormComponent.buildEmailForm(this.fb),
      );
    }

    branchForm.setValue({ ...template, branchUuid });
  }

  protected removeBranchForm(): void {
    const branchForm = this.selectedBranchForm();
    if (!branchForm) return;

    const index = this.form().controls.indexOf(branchForm);
    this.form().removeAt(index);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.array<ContractorInvoiceFormGroup>([]);
  }
}
